/*
 * The parts of <shopify-account> that no selector reaches from outside.
 *
 * The element exposes two ::part()s — signed-out-avatar and dialog — and
 * everything the sheet wears that can be done through those, or through the
 * published --shopify-account-* / --shopify-login-form-* properties, is done in
 * synonym-bridge.css. This file is only for what is left: the cross's artwork,
 * the rule either side of "OR", the email field's wording and face, and the
 * marketing checkbox. All of them live inside a shadow root with no part on it.
 *
 * Two roots are involved, and they are not the same one:
 *
 *   shopify-account      the panel chrome — the header and its cross
 *   shopify-login-form   the form — divider, email field, consent checkbox
 *
 * The second is nested two deep (account > login-form-loader > login-form) and
 * is built lazily, so it does not exist when the page loads and often not until
 * the sheet is first opened. Hence the rescan rather than a single pass.
 *
 * Everything here is expressed as a stylesheet wherever it can be, because the
 * component re-renders its own content and a rule survives that where an edited
 * node would have to be re-edited. The two strings that cannot be set in CSS —
 * the field's label and placeholder — are written back under an observer for
 * exactly that reason.
 *
 * These are the component's private class names. If Shopify renames them these
 * rules match nothing and the sheet falls back to how it shipped, which is the
 * failure mode worth having.
 */

(function () {
  'use strict';

  var INK = '29, 28, 27';
  var PAPER = '244, 243, 243';

  /* The cart drawer's own cross, path and viewBox verbatim, so the two are the
     same drawing rather than two drawings at the same size. The component's is
     a filled glyph in a 24-unit box whose arms are 1.5 units thick — scaled up
     to match the cart's 18.8px width it came out at 2.7px of ink against the
     cart's 1.1px, which is the "thicker" that no amount of sizing would fix.
     Masking is what lets the colour still come from currentColor. */
  var CROSS =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
    "viewBox='0 0 18 17'%3E%3Cpath fill='%23000' d='M.865 15.978a.5.5 0 0 0 " +
    ".707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 " +
    "0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 " +
    "1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z'/%3E%3C/svg%3E\")";

  /* The signed-in trigger's two possible faces: initials, or the Shop coin. */
  var AVATAR = '.account-button__avatar, .account-button__coin-front,' +
    ' .account-button__coin-back';
  var SIGNED_IN = '.account-button:has(.account-button__avatar,' +
    ' .account-button__coin-front)';

  var ACCOUNT_CSS = [
    /* Orders / Profile are hidden while nobody is signed in: both go somewhere
       that requires an account, so offered to a signed-out reader they are two
       buttons whose only outcome is being bounced to the sign-in form already
       open above them.

       The selector self-gates on the component's own state rather than on
       anything asserted from here — it marks the login slot [hidden] once there
       is a customer — so signing in without a page load brings them back. */
    '.account__content:has(> .account__login-slot:not([hidden])) .stack-inline',
    '  { display: none; }',

    /* The cross, redrawn. On ::after rather than the button itself because the
       component's own `.button.subdued:hover` sets background-color at three
       classes and would blank the mask's ink on hover. */
    '.account__close svg { display: none; }',
    '.account__close::after {',
    '  content: "";',
    '  width: 20px;',
    '  height: 20px;',
    '  background-color: currentColor;',
    '  -webkit-mask: ' + CROSS + ' center / 20px 20px no-repeat;',
    '  mask: ' + CROSS + ' center / 20px 20px no-repeat;',
    '}',

    /* Signed in, the component drops the slotted label and draws a filled
       circle in the bar instead — initials, or a Shop coin for a recognised
       customer. The bar has one vocabulary and a portrait is not in it, so the
       trigger goes on saying the same word it says signed out.

       The light DOM's slot only fills the signed-out state, so the word has to
       come from a ::after here, and the two are kept apart by :has() — the
       label appears only where an avatar exists to replace, and the slot is
       hidden in that case so the two can never both print.

       The button's own box is neutralised again for the same reason it is in
       synonym-bridge.css: it is drawn round and 44px for an icon, and this is a
       word. Restated rather than relied upon, because the part name the bridge
       hooks is "signed-out-avatar" and there is no guarantee it is still on the
       button once somebody signs in. */
    AVATAR + ' { display: none; }',
    SIGNED_IN + ' slot[name="signed-out-avatar"] { display: none; }',
    SIGNED_IN + ' {',
    '  width: auto;',
    '  height: auto;',
    '  min-width: 0;',
    '  min-height: 0;',
    '  padding: 0;',
    '  border-radius: 0;',
    '  background: none;',
    '  box-shadow: none;',
    '}',
    SIGNED_IN + '::after {',
    '  content: "Your Account";',
    '  font-family: "Matter Mono", "IBM Plex Mono", "Courier New", monospace;',
    '  font-size: 16px;',
    '  line-height: 24px;',
    '  letter-spacing: 0.05em;',
    '  text-transform: uppercase;',
    '  white-space: nowrap;',
    '  color: inherit;',
    '}',

    /* The sheet's header names the account by its address, not by a greeting.
       The address was already printed under the greeting, so this is a matter of
       dropping one line and letting the other carry the header — in ink rather
       than the subdued grey it wore as a subtitle, since it is now the title.

       Scoped to --has-email so that a session with no address to show is left
       with its greeting rather than an empty header. */
    '.account__header-content--has-email .account__heading { display: none; }',
    '.account__email {',
    /* The heading's own treatment, which is what the address now is: Matter
       Regular 24/32, and set as written rather than in the mono's caps — the
       component uppercases it as body text, and an address shouted at 24px is
       not what the other two sheets do with their titles. */
    '  font-family: "Matter", serif;',
    '  font-size: 24px;',
    '  line-height: 32px;',
    '  text-transform: none;',
    '  letter-spacing: 0;',
    '  color: rgb(' + INK + ');',
    /* Shipped as a single nowrap line that truncates with an ellipsis. A
       truncated address is worse than a wrapped one — it is the only thing
       identifying whose account this is — so it wraps, and the <wbr> put in by
       nameTheAccount() below is the only place it can break. */
    '  white-space: normal;',
    '  overflow: visible;',
    '  text-overflow: clip;',
    /* Last resort only. The <wbr> is the sole break opportunity in an address,
       so break-word changes nothing about where a normal one wraps — it just
       stops a local part longer than the panel from running out of the card
       rather than folding. */
    '  overflow-wrap: break-word;',
    '}',

    /* Orders / Profile: the outline darkens to full ink on hover, at the same
       0.24s the email and country-search fields use. The component only offered
       a background tint, which is invisible here — the tint token is zeroed so
       the close cross has no disc. */
    '.button.outline { transition: border-color 0.24s ease-in-out; }',
    '.button.outline:hover { border-color: rgb(' + INK + '); }'
  ].join('\n');

  var LOGIN_CSS = [
    /* No rule either side of "OR" — the word is enough of a divide, and the two
       hairlines are the only strokes in the panel that are not drawing an edge.
       The token they use is shared with the email field's border, so this has
       to be said here rather than by nulling a colour. */
    '.login-form__divider-line { display: none; }',
    /* The row is a flex line that was justified by the two rules growing to
       fill it; with them gone the word falls to the start, so it is centred
       outright. */
    '.login-form__divider { justify-content: center; }',

    /* The field reads as prose, not as a label, so it takes the reading face
       rather than the mono. */
    '.login-form__email-input {',
    '  font-family: "Matter", serif;',
    '  text-transform: none;',
    '  letter-spacing: 0;',
    /* --shadow-control puts a drop-shadow *filter* on the input — not a
       box-shadow, which is why `box-shadow` reads as none while a shadow is
       plainly there. */
    '  filter: none;',
    /* The 16px/4px split was headroom for the label to rise into. With the
       label gone there is nothing to make room for, and an even block centres
       the text in the field\'s own 52px. */
    '  padding-block: 0;',
    '}',

    /* The name stops being a floating label and becomes an ordinary
       placeholder: on screen while the field is empty, gone the moment somebody
       types, and never shrinking into the corner.
       
       The label is clipped rather than removed. It is the field\'s accessible
       name, and a field named only by its placeholder has no name at all once
       there is text in it — the same reasoning as the country search\'s label
       in synonym-bridge.css. */
    '.login-form__email-label {',
    '  position: absolute;',
    '  width: 1px;',
    '  height: 1px;',
    '  padding: 0;',
    '  margin: -1px;',
    '  overflow: hidden;',
    '  clip: rect(0 0 0 0);',
    '  clip-path: inset(50%);',
    '  white-space: nowrap;',
    '  border: 0;',
    '}',

    /* Shopify hides the placeholder outright — `color: transparent` — because
       the label was doing that job. It has to be given back its ink. */
    '.login-form__email-input::placeholder {',
    '  font-family: "Matter", serif;',
    '  text-transform: none;',
    '  letter-spacing: 0;',
    '  color: rgb(' + INK + ');',
    '  opacity: 0.7;',
    '}',

    /* The consent line in the cart's caption face — Matter Mono 12/0.6px caps,
       the same as PRODUCT and TOTAL over the item table. */
    '.login-form__marketing-consent-label {',
    '  font-family: "Matter Mono", "IBM Plex Mono", "Courier New", monospace;',
    '  font-size: 12px;',
    '  letter-spacing: 0.6px;',
    '  text-transform: uppercase;',
    '}',

    /* A rounded rectangle rather than a disc. The radius is derived from
       --shopify-login-form-radius-input, which is the email field's 99px pill
       and cannot be lowered without flattening the field too, so it is stated
       outright. */
    '.login-form__marketing-consent-visual { border-radius: 3px; }',

    /* Filled in ink, not accent. The component fills the box with an inset ring
       half its own width; keeping that construction means the transition still
       runs. The tick has to flip to paper to stay legible on it. */
    '.login-form__marketing-consent-checkbox:checked',
    '  + .login-form__marketing-consent-visual {',
    '  box-shadow: 0 0 0 calc(var(--font-size-sm) / 2) rgb(' + INK + ') inset;',
    '}',
    '.login-form__marketing-consent-checkbox:checked:focus-visible',
    '  + .login-form__marketing-consent-visual {',
    '  box-shadow: 0 0 0 calc(var(--font-size-sm) / 2) rgb(' + INK + ') inset,',
    '              var(--shadow-focus-ring-accent);',
    '}',
    '.login-form__marketing-consent-icon { color: rgb(' + PAPER + '); }'
  ].join('\n');

  var EMAIL_LABEL = 'Email Address';

  function adopt(root, css, flag) {
    if (root[flag]) return true;
    root[flag] = true;
    if (typeof CSSStyleSheet === 'function' && 'replaceSync' in CSSStyleSheet.prototype) {
      try {
        var sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat([sheet]);
        return true;
      } catch (e) {
        /* fall through to a <style> */
      }
    }
    var el = document.createElement('style');
    el.textContent = css;
    root.appendChild(el);
    return true;
  }

  /* querySelector does not cross shadow boundaries, so the walk is by hand. */
  function deepFind(root, tag) {
    var direct = root.querySelector(tag);
    if (direct) return direct;
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) {
        var found = deepFind(all[i].shadowRoot, tag);
        if (found) return found;
      }
    }
    return null;
  }

  function nameTheField(root) {
    var input = root.querySelector('.login-form__email-input');
    var label = root.querySelector('.login-form__email-label');
    if (input && input.placeholder !== EMAIL_LABEL) input.placeholder = EMAIL_LABEL;
    if (label && label.textContent !== EMAIL_LABEL) label.textContent = EMAIL_LABEL;
    return !!input;
  }

  /*
   * Gives the address in the sheet's header somewhere to break.
   *
   * An email has no spaces in it, so left alone a long one either overflows or
   * gets cut with an ellipsis — and a truncated address is the one thing here
   * that must stay readable, since it is what tells you whose account you are
   * looking at. A <wbr> before the @ is a break opportunity and nothing else:
   * it costs no space, and because it is the only one in the string the address
   * can only ever break in that one place, giving joel / @thisisforest.com
   * rather than a split through the middle of a word.
   */
  function nameTheAccount(root) {
    var email = root.querySelector('.account__email');
    if (!email) return;
    /* Already done — and the guard matters, because this runs from a mutation
       observer that its own edit would otherwise wake. */
    if (email.querySelector('wbr')) return;
    var text = (email.textContent || '').trim();
    var at = text.indexOf('@');
    if (at < 1) return;
    email.textContent = text.slice(0, at);
    email.appendChild(document.createElement('wbr'));
    email.appendChild(document.createTextNode(text.slice(at)));
  }

  function watchTheAccount(root) {
    if (root.__synonymAccountWatched || typeof MutationObserver !== 'function') return;
    root.__synonymAccountWatched = true;
    nameTheAccount(root);
    /* The sheet rebuilds its header when the session changes — signing in
       without a page load, most obviously — so the break is re-inserted rather
       than done once. */
    new MutationObserver(function () { nameTheAccount(root); })
      .observe(root, { childList: true, subtree: true, characterData: true });
  }

  function watchTheField(root) {
    if (root.__synonymFieldWatched || typeof MutationObserver !== 'function') return;
    root.__synonymFieldWatched = true;
    /* The form re-renders on its own — validation, state changes — and puts the
       shipped wording back each time, so the rename is re-applied rather than
       done once. */
    new MutationObserver(function () { nameTheField(root); })
      .observe(root, { childList: true, subtree: true, characterData: true });
  }

  var done = false;

  function scan() {
    var hosts = document.querySelectorAll('shopify-account');
    if (!hosts.length) return false;
    var complete = true;
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;
      if (!root) { complete = false; continue; }
      adopt(root, ACCOUNT_CSS, '__synonymAccountCss');
      watchTheAccount(root);

      var form = deepFind(root, 'shopify-login-form');
      if (form && form.shadowRoot) {
        adopt(form.shadowRoot, LOGIN_CSS, '__synonymLoginCss');
        watchTheField(form.shadowRoot);
        if (!nameTheField(form.shadowRoot)) complete = false;
      } else {
        complete = false;
      }
    }
    return complete;
  }

  /* The login form arrives on its own schedule, so this polls — but only in
     bounded bursts, and it stops for good once both roots are dressed. A burst
     is re-armed by a click anywhere in the bar, which is the gesture that opens
     the sheet in the first place. */
  function burst(ticks) {
    if (done) return;
    if (scan()) { done = true; return; }
    if (ticks <= 0) return;
    setTimeout(function () { burst(ticks - 1); }, 200);
  }

  /*
   * The empty cart's "Log in to check out faster" opens the sheet instead of
   * leaving for Shopify's hosted sign-in page.
   *
   * The whole point of the sheet is that signing in does not take a reader off
   * the shop, and that link is the one place left that still would — it comes
   * out of a translation string, `sections.cart.login.paragraph_html`, with the
   * href baked into the copy, so there is no template to change and it has to be
   * caught at the click.
   *
   * showModal() is the element's own public method. The alternative was reaching
   * into the shadow root for its button and clicking it, which is what the rest
   * of this file has to resort to; here there is a front door.
   *
   * Delegated on document, not bound to the link, because Dawn re-renders the
   * cart drawer's contents from the section on every change and any listener
   * attached to that anchor would be thrown away with it.
   *
   * Nothing is prevented unless there is a sheet to open and it agrees to open:
   * on a viewport where neither trigger is visible, or if showModal() rejects,
   * the click is left alone and the link does what it always did.
   */
  /* Any upgraded instance will do. The first attempt at this asked for one
     whose trigger was on screen, which sounds right and is not: the panel is a
     modal <dialog> in the top layer, so it does not care where — or whether —
     its own button is drawn. On a product page it measured nothing at all, the
     bar being pinned and unrevealed and the phone panel shut, and the link fell
     through to the hosted page. The bar's copy is preferred over the phone
     panel's only so that closing the sheet returns focus somewhere sensible. */
  function accountHost() {
    var hosts = document.querySelectorAll('shopify-account');
    var fallback = null;
    for (var i = 0; i < hosts.length; i++) {
      if (!hosts[i].shadowRoot) continue;
      if (!hosts[i].closest('.site-menu')) return hosts[i];
      fallback = fallback || hosts[i];
    }
    return fallback;
  }

  function interceptCartLogin(event) {
    var link = event.target.closest && event.target.closest('a');
    if (!link) return;
    if (!link.closest('cart-drawer')) return;
    /* Match on where it goes rather than on a class, so it still holds if the
       translation's markup changes. */
    if (!/\/(account|customer_authentication)\/?(login|)/.test(link.pathname || '')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;

    var host = accountHost();
    if (!host || typeof host.showModal !== 'function') return;

    event.preventDefault();

    /* The drawer first: two overlays at once would leave the reader looking at
       a sign-in sheet floating over a cart they did not close. */
    var drawer = link.closest('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();

    /* showModal() resolves whether or not the panel actually came up, so the
       fallback cannot hang off a rejection — what matters is whether the dialog
       is open shortly afterwards. If it is not, the reader gets the hosted page
       rather than a click that did nothing. 600ms clears the 300ms entrance
       with room to spare. */
    var settle = function () {
      var dialog = host.shadowRoot && host.shadowRoot.querySelector('dialog');
      if (!dialog || !dialog.open) window.location.href = link.href;
    };
    Promise.resolve(host.showModal()).then(
      function () { setTimeout(settle, 600); },
      function () { window.location.href = link.href; }
    );
  }

  function start() {
    burst(50);
    document.addEventListener('click', function () {
      if (!done) burst(25);
    }, true);
    document.addEventListener('click', interceptCartLogin);
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('shopify-account').then(start);
  } else {
    start();
  }
})();
